import { Observable } from "rxjs";
import { Environment } from "src/libs/environment";
import { inject, singleton } from "tsyringe";

import { HttpUtils } from "src/infra/http/utils";
import { HttpRequestClient, RequestOptions } from "src/infra/http/types";

@singleton()
export class HttpClient implements HttpRequestClient {
    activeSSO = false;
    constructor(@inject("HttpUtils") public httpUtils: HttpUtils) {
        this.activeSSO = Environment.bool("ACTIVE_SSO", true);
    }

    get hostEnv() {
        if (this.activeSSO) {
            return process.env.REACT_APP_SERVER_MANAGEMENT_URL;
        }
        return process.env.REACT_APP_SERVER_URL || "";
    }

    get msHostEnv() {
        return process.env.REACT_APP_APIMGMT_URL;
    }

    get hostAccessManagementEnv() {
        return process.env.REACT_APP_MANAGEMENT_ACCESS_URL;
    }

    getJSON<T>(url: string, options: RequestOptions): Observable<T> {
        const {
            params = {},
            isPublicRequest = true,
            host = this.hostEnv ?? "",
            customHeader = null,
        } = options;

        const hasParams = Object.keys(params).length > 0;
        const queryParams = new URLSearchParams(params as Record<string, any>);
        const endpoint = hasParams
            ? this.httpUtils.getUrl(`${url}?${queryParams}`, host)
            : this.httpUtils.getUrl(url, host);
        const headers = this.httpUtils.getRealHeaders(
            customHeader,
            isPublicRequest
        );
        return this.httpUtils.createCancellableRequest<T>({
            url: endpoint,
            method: "get",
            headers,
        });
    }

    get<T>(url: string, options: RequestOptions): Observable<T> {
        const {
            params = {},
            isPublicRequest = true,
            host = this.hostEnv ?? "",
            customHeader = null,
        } = options;
        const hasParams = Object.keys(params).length > 0;
        const queryParams = new URLSearchParams(params as Record<string, any>);
        const endpoint = hasParams
            ? this.httpUtils.getUrl(`${url}?${queryParams}`, host)
            : this.httpUtils.getUrl(url, host);
        const headers = this.httpUtils.getRealHeaders(
            customHeader,
            isPublicRequest
        );

        return this.httpUtils.createCancellableRequest({
            url: endpoint,
            method: "get",
            headers,
        });
    }

    post<T>(url: string, options: RequestOptions): Observable<T> {
        const {
            params = {},
            data = {},
            isPublicRequest = true,
            host = this.hostEnv ?? "",
            customHeader = null,
        } = options;
        const hasParams = Object.keys(params).length > 0;
        const queryParams = new URLSearchParams(params as Record<string, any>);
        const endpoint = hasParams
            ? this.httpUtils.getUrl(`${url}?${queryParams}`, host)
            : this.httpUtils.getUrl(url, host);
        const headers = this.httpUtils.getRealHeaders(
            customHeader,
            isPublicRequest
        );
        return this.httpUtils.createCancellableRequest({
            url: endpoint,
            method: "post",
            headers,
            body: data as never,
        });
    }

    put<T>(url: string, options: RequestOptions): Observable<T> {
        const {
            params = {},
            data = {},
            isPublicRequest = true,
            host = this.hostEnv ?? "",
            customHeader = null,
        } = options;
        const hasParams = Object.keys(params).length > 0;
        const queryParams = new URLSearchParams(params as Record<string, any>);
        const endpoint = hasParams
            ? this.httpUtils.getUrl(`${url}?${queryParams}`, host)
            : this.httpUtils.getUrl(url, host);
        const headers = this.httpUtils.getRealHeaders(
            customHeader,
            isPublicRequest
        );
        return this.httpUtils.createCancellableRequest({
            url: endpoint,
            method: "put",
            headers,
            body: data as never,
        });
    }

    patch<T>(url: string, options: RequestOptions): Observable<T> {
        const {
            params = {},
            data = {},
            isPublicRequest = true,
            host = this.hostEnv ?? "",
            customHeader = null,
        } = options;
        const hasParams = Object.keys(params).length > 0;
        const queryParams = new URLSearchParams(params as Record<string, any>);
        const endpoint = hasParams
            ? this.httpUtils.getUrl(`${url}?${queryParams}`, host)
            : this.httpUtils.getUrl(url, host);
        const headers = this.httpUtils.getRealHeaders(
            customHeader,
            isPublicRequest
        );
        return this.httpUtils.createCancellableRequest({
            url: endpoint,
            method: "patch",
            headers,
            body: data as never,
        });
    }

    delete<T>(url: string, options: RequestOptions): Observable<T> {
        const {
            isPublicRequest = true,
            host = this.hostEnv ?? "",
            customHeader = null,
        } = options;
        const endpoint = this.httpUtils.getUrl(url, host);
        const headers = this.httpUtils.getRealHeaders(
            customHeader,
            isPublicRequest
        );
        return this.httpUtils.createCancellableRequest({
            url: endpoint,
            method: "delete",
            headers,
        });
    }
}
